<template>
  <div class="home">
    <TheHeader />

    <!-- Hero Section -->
    <div class="relative bg-gray-900 min-h-[700px] flex items-center justify-center pb-20 pt-32">
       <!-- Background Image -->
      <div class="absolute inset-0 bg-secondary">
         <!-- Use a specific image if available, or keep gradient/placeholder -->
         <img v-if="heroImage" :src="heroImage" class="w-full h-full object-cover opacity-40 mix-blend-overlay">
         <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-90"></div>
      </div>
      
      <!-- Content -->
      <div id="vuelos" class="relative z-10 w-full px-4">
        <div class="text-center mb-10">
           <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg tracking-tight">
             {{ $t('home.hero_title') }}
           </h1>
           <p class="text-xl text-blue-100 max-w-2xl mx-auto font-light">
             {{ $t('home.hero_desc') }}
           </p>
        </div>

        <!-- New Quote Form -->
        <FlightQuoteForm />
      </div>
    </div>

    <!-- Flight Promotions Section -->
    <FlightPromotionsSection />

    <!-- Wave Divider with Icon -->
    <div class="relative w-full leading-[0] bg-white z-10">
        <svg class="relative block w-full h-[60px] md:h-[120px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" class="fill-gray-50"></path>
        </svg>
        <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded-full shadow-xl border border-gray-100 z-20 hidden md:flex items-center justify-center">
             <span class="material-icons text-primary text-4xl">travel_explore</span>
        </div>
    </div>

    <!-- Featured Tours Section -->
    <section id="tours" v-if="loading || tours.length > 0" class="pb-16 pt-8 bg-gray-50 relative z-0">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
           <span class="text-primary font-bold tracking-wider uppercase text-sm">{{ $t('home.featured_tours_subtitle') }}</span>
           <h2 class="text-3xl font-bold mt-2 text-gray-900">{{ $t('home.featured_tours_title') }}</h2>
        </div>

        <!-- Skeleton Loading State -->
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="n in 3" :key="`skeleton-${n}`" class="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
             <base-skeleton height="250px" borderRadius="0" />
             <div class="p-6 space-y-4">
                <base-skeleton width="60%" height="24px" />
                <base-skeleton width="90%" height="16px" />
                <base-skeleton width="40%" height="16px" />
                <div class="flex justify-between items-center pt-4">
                   <base-skeleton width="80px" height="30px" />
                   <base-skeleton width="100px" height="40px" borderRadius="999px" />
                </div>
             </div>
          </div>
        </div>

        <!-- Real Content -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
           <div v-for="tour in tours" :key="tour.id" class="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-100">
              <div class="h-64 bg-gray-300 relative overflow-hidden">
                 <!-- Image Placeholder -->
                 <img v-if="tour.image" :src="tour.image" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" @click="goToDetail(tour.id)">
                 <div v-else class="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400" @click="goToDetail(tour.id)">
                    <span class="material-icons text-4xl">photo</span>
                 </div>
                 
                 <div class="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-900 font-bold px-3 py-1 rounded-full text-xs shadow-sm uppercase tracking-wider">
                    {{ tour.days }} {{ $t('common.days') }}
                 </div>
              </div>
              <div class="p-6">
                 <h3 class="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">{{ tour.title }}</h3>
                 <p class="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed opacity-80">{{ tour.description }}</p>
                 <div class="flex justify-between items-end border-t border-gray-100 pt-4 mt-4">
                    <div>
                        <span class="text-xs text-gray-400 block uppercase font-bold tracking-wide">{{ $t('common.from') }}</span>
                        <span class="text-2xl font-bold text-secondary">${{ tour.price }} <span class="text-xs font-normal text-gray-500">{{ $t('common.currency') }}</span></span>
                    </div>
                    <button @click="goToDetail(tour.id)" class="bg-gray-100 text-gray-700 px-5 py-2 rounded-lg font-medium hover:bg-secondary hover:text-white transition-all text-sm">
                        {{ $t('common.details') }}
                    </button>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </section>

    <TheFooter />
  </div>
</template>

<script>
import TheHeader from '@/components/layout/TheHeader.vue'
import TheFooter from '@/components/layout/TheFooter.vue'
import BaseSkeleton from '@/components/ui/BaseSkeleton.vue'
import FlightQuoteForm from '@/components/forms/FlightQuoteForm.vue'
import FlightPromotionsSection from '@/components/home/FlightPromotionsSection.vue'
import packageService from '@/services/packageService'

export default {
  name: 'Home',
  components: {
    TheHeader,
    TheFooter,
    BaseSkeleton,
    FlightQuoteForm,
    FlightPromotionsSection
  },
  data() {
    return {
      loading: true,
      heroImage: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?ixlib=rb-4.0.3&auto=format&fit=crop&w=2021&q=80', // Free unsplash image
      tours: []
    }
  },
  async mounted() {
    try {
      const response = await packageService.getPackages()
      this.tours = response.slice(0, 6).map(tour => ({
        ...tour,
        image: tour.full_image || tour.image
      }))
    } catch (error) {
      console.error('Error fetching tours:', error)
      this.tours = []
    } finally {
      this.loading = false
    }
  },
  methods: {
    goToDetail(id) {
        this.$router.push({ name: 'PackageDetail', params: { id } })
    }
  }
}
</script>
